<template>
	<div class="terminal-takeover" v-if="showTransition">
		<div class="takeover-glitch-overlay"></div>
		<div class="takeover-scanlines"></div>
		<div class="takeover-text">INTERCEPTING SIGNAL</div>
	</div>
	<div id="terminal-view" class="terminal-bg">
		<div class="terminal-new-messages" v-if="newMessages > 0" @click="jumpToBottom">
			&#9660; {{ newMessages }} new message{{ newMessages !== 1 ? "s" : "" }}
		</div>
		<aside class="terminal-sidebar">
			<router-link to="/status" class="terminal-back">&lt; EXIT</router-link>
			<div class="terminal-sidebar-header">FILE EXPLORER</div>
			<ul class="terminal-file-list">
				<li v-for="folder in filesystem.folders" :key="folder.name" class="terminal-folder">
					<span class="folder-name">{{ folder.name }}</span>
					<ul>
						<li
							v-for="file in folder.files.filter(f => !f.hidden)"
							:key="file.name"
							:class="{ 'file-locked': file.locked, 'file-clickable': !file.locked }"
							@click="!file.locked && onFileClick(file)"
						>
							{{ file.name }}
						</li>
						<li v-for="sub in folder.subfolders || []" :key="sub.name" class="terminal-subfolder">
							<span class="subfolder-name" @click="toggleSubfolder(sub.name)">
								{{ sub.name }} {{ openSubfolders[sub.name] ? "[-]" : "[+]" }}
							</span>
							<ul v-if="openSubfolders[sub.name]">
								<li
									v-for="file in sub.files"
									:key="file.name"
									:class="{ 'file-locked': file.locked, 'file-clickable': !file.locked }"
									@click="!file.locked && onFileClick(file)"
								>
									{{ file.name }}
								</li>
							</ul>
						</li>
					</ul>
				</li>
			</ul>
			<!-- GREEBLINGS -->
			<div class="gb-panel">
				<div class="gb-header">STATUS</div>
				<div class="gb-row gb-conn-row">
					<span class="gb-dot" :class="{ 'gb-conn-flicker': gbConnFlicker }">●</span>
					<span class="gb-conn-label">CONNECTED // HATHOR-ALEPH</span>
				</div>
				<div class="gb-row">
					<span class="gb-key">SIG</span>
					<span class="gb-val">{{ gbSignalBar }}</span>
				</div>
				<div class="gb-row">
					<span class="gb-key">USR</span>
					<span class="gb-val">{{ gbUsers }}</span>
				</div>
				<div class="gb-row">
					<span class="gb-key">UP</span>
					<span class="gb-val">{{ gbUptimeStr }}</span>
				</div>
				<div class="gb-row">
					<span class="gb-key">OMNI</span>
					<span class="gb-val" :class="{ 'gb-warn': gbOmniStatus === 'DEGRADED' }">{{
						gbOmniStatus
					}}</span>
				</div>
			</div>
			<div class="gb-panel">
				<div class="gb-header">NETWORK</div>
				<div class="gb-row">
					<span class="gb-key">LAT</span>
					<span class="gb-val" :class="{ 'gb-warn': gbLatency > 500 }">{{ gbLatency }}ns</span>
				</div>
				<div class="gb-row">
					<span class="gb-key">BW</span>
					<span class="gb-val">↑{{ gbBandwidthUp }}kb ↓{{ gbBandwidthDown }}kb</span>
				</div>
				<div class="gb-row gb-hop-row">
					<span class="gb-key">HOP</span>
					<span class="gb-val gb-hop">{{ gbRouteHop }}</span>
				</div>
				<div class="gb-row">
					<span class="gb-key">ENC</span>
					<span class="gb-val gb-enc" :class="{ 'gb-enc-pulse': gbEncryptPulse }">█ ACTIVE</span>
				</div>
				<div class="gb-row gb-hex-row">
					<span class="gb-hex">{{ gbNetworkHex }}</span>
				</div>
			</div>
			<div class="gb-panel">
				<div class="gb-header">SYSTEM</div>
				<div class="gb-row">
					<span class="gb-key">MEM</span>
					<span class="gb-val">{{ gbMemBar }} {{ gbMem }}%</span>
				</div>
				<div class="gb-row">
					<span class="gb-key">PKT</span>
					<span class="gb-val" :class="{ 'gb-warn': parseFloat(gbPacketLoss) > 1 }"
						>{{ gbPacketLoss }}%</span
					>
				</div>
				<div class="gb-row">
					<span class="gb-key">BUF</span>
					<span class="gb-val">{{ gbBufferBar }} {{ gbBuffer }}/4096</span>
				</div>
				<div class="gb-row">
					<span class="gb-key">SUM</span>
					<span class="gb-val gb-sum" :class="{ 'gb-flicker': gbChecksumFlicker }">{{
						gbChecksum
					}}</span>
				</div>
				<div class="gb-row gb-ps-row">
					<span class="gb-key">PS</span>
					<span class="gb-ps-list">
						<span v-for="p in gbProcessList" :key="p" class="gb-ps-entry">{{ p }}</span>
					</span>
				</div>
			</div>
			<div class="gb-panel">
				<div class="gb-header">SESSION</div>
				<div class="gb-row">
					<span class="gb-key">LAST</span>
					<span class="gb-val">5017.3.09 // 02:14 UST</span>
				</div>
				<div class="gb-row">
					<span class="gb-key">TOK</span>
					<span class="gb-val gb-tok">{{ gbSessionToken }}</span>
				</div>
			</div>
			<div class="terminal-clock">
				<div class="clock-label">CrST</div>
				<div class="clock-time">{{ clock }}</div>
				<div class="clock-date">{{ clockDate }}</div>
			</div>
		</aside>
		<div class="terminal-main">
			<div class="terminal-inner-row">
				<div class="terminal-scale-sidebar"></div>
				<!-- Shell mode (default) -->
				<div
					class="terminal-pane-grid"
					:class="'pane-count-' + panes.length"
					v-show="currentMode === 'shell'"
				>
					<template v-for="(pane, idx) in panes" :key="pane.id">
						<div class="split-divider" v-if="idx > 0"></div>
						<div
							class="terminal-shell split-pane"
							:class="{ 'split-active': activePaneIdx === idx }"
							@click="selectPane(idx)"
						>
							<div class="shell-output" :id="'shell-output-' + pane.id"></div>
							<!-- ATHENA calculator panel -->
							<div class="athena-panel" v-if="pane.program === 'athena' && athenaActive">
								<div class="athena-header">
									<div class="athena-title">ATHENA</div>
								</div>
								<div class="athena-inputs-row">
									<!-- Pilot Level: label centered over grit tag + stepper -->
									<div class="athena-field-group">
										<label class="athena-label">PILOT LEVEL</label>
										<div class="athena-pilot-row">
											<div class="athena-stepper">
												<button
													class="athena-step-btn"
													:disabled="athenaLocked"
													@click="stepAthena('athenaLevel', 1, 0, 12)"
												>
													&#9650;
												</button>
												<input
													class="athena-input"
													type="number"
													v-model.number="athenaLevel"
													min="0"
													max="12"
													:disabled="athenaLocked"
													@change="calculateAthena"
												/>
												<button
													class="athena-step-btn"
													:disabled="athenaLocked"
													@click="stepAthena('athenaLevel', -1, 0, 12)"
												>
													&#9660;
												</button>
											</div>
											<div class="athena-grit-tag">GRIT: {{ athenaGrit }}</div>
										</div>
									</div>
									<!-- Target Evasion: label centered over stepper -->
									<div class="athena-field-group">
										<label class="athena-label">TARGET EVASION</label>
										<div class="athena-stepper">
											<button
												class="athena-step-btn"
												:disabled="athenaLocked"
												@click="stepAthena('athenaEvasion', 1, 0, 20)"
											>
												&#9650;
											</button>
											<input
												class="athena-input"
												type="number"
												v-model.number="athenaEvasion"
												min="0"
												max="20"
												:disabled="athenaLocked"
												@change="calculateAthena"
											/>
											<button
												class="athena-step-btn"
												:disabled="athenaLocked"
												@click="stepAthena('athenaEvasion', -1, 0, 20)"
											>
												&#9660;
											</button>
										</div>
									</div>
									<!-- Accuracy/Difficulty: label centered over stepper -->
									<div class="athena-field-group">
										<label class="athena-label">ACCURACY / DIFFICULTY</label>
										<div class="athena-stepper">
											<button
												class="athena-step-btn"
												:disabled="athenaLocked"
												@click="stepAthena('athenaAccuracy', 1, -10, 10)"
											>
												&#9650;
											</button>
											<input
												class="athena-input"
												type="number"
												v-model.number="athenaAccuracy"
												min="-10"
												max="10"
												:disabled="athenaLocked"
												@change="calculateAthena"
											/>
											<button
												class="athena-step-btn"
												:disabled="athenaLocked"
												@click="stepAthena('athenaAccuracy', -1, -10, 10)"
											>
												&#9660;
											</button>
										</div>
									</div>
								</div>
								<div class="athena-formula-row">
									<span class="athena-formula-label">ATTACK</span>
									<span class="athena-formula-tag">{{ athenaFormula }}</span>
								</div>
								<div class="athena-results-row">
									<div
										class="athena-result-box athena-result-hit"
										:class="{ 'athena-flipping': athenaFlipping.hit }"
									>
										TO HIT: {{ athenaToHitDisplay }}%
									</div>
									<div
										class="athena-result-box athena-result-crit"
										:class="{ 'athena-flipping': athenaFlipping.crit }"
									>
										CRIT CHANCE: {{ athenaCritDisplay }}%
									</div>
								</div>
							</div>
							<div :id="'athena-output-' + pane.id"></div>
							<div :id="'shell-output-post-' + pane.id"></div>
							<div class="shell-input-row">
								<span class="shell-prompt">{{ shellCwd }} $&gt;</span>
								<input
									:ref="
										el => {
											if (el) paneInputRefs[pane.id] = el;
										}
									"
									v-model="pane.inputValue"
									@keydown.enter="executePaneCommand(idx)"
									@keydown.up.prevent="historyUp"
									@keydown.down.prevent="historyDown"
									@keydown.tab.prevent="tabCompletePane(idx)"
									@input="resetTabCycle"
									class="shell-input"
									type="text"
									spellcheck="false"
									autocomplete="off"
								/>
							</div>
							<div
								class="athena-hint"
								v-if="pane.locked && pane.program === 'athena' && !athenaLocked"
							>
								type "exit" or "quit" to disconnect
							</div>
							<div class="shell-locked-hint" v-if="pane.locked && pane.program !== 'athena'">
								type "exit" or "quit" to return to shell
							</div>
						</div>
					</template>
				</div>
				<!-- Log viewer mode -->
				<div class="terminal-log-viewer" v-if="currentMode === 'logview'">
					<div class="log-path">/ usr / localdata / {{ viewingLog.name }}</div>
					<div class="log-separator"></div>
					<pre class="log-content">{{ logContent }}</pre>
				</div>
				<div class="terminal-right-col">
					<div class="terminal-scale-sidebar"></div>
					<img
						src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABEAAAAQCAYAAADwMZRfAAAAj0lEQVQ4ja2SYQ6AIAhGoXlnOYKcmpYbDQjNXP7K8fH2JOCPgyLyGUNEdxMz47EpgvayC3GgLUhrzd23TSxoCCGiZWBJmnXy+IynOW9iC3rPjGLuSArRwIGyXEnI9tsBA0C0jrXWzGBljXuembuJUmPjdLD26ExsQzQaWumu2F88NYpb6kyuognEJ7w/CQBOqJ04k+FE4rIAAAAASUVORK5CYII="
						alt=""
						class="terminal-corner-img"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import nfoArt from "@/data/terminal/scripts/nfo.txt?raw";
import sscArt from "@/data/terminal/scripts/ssc.txt?raw";
import athenaMessagesRaw from "@/data/terminal/scripts/athena-messages.txt?raw";

import filesystemData from "@/data/terminal/filesystem.json";

const logFiles = import.meta.glob("@/data/terminal/filesystem/*.log", {
	query: "?raw",
	import: "default",
	eager: true,
});
const scriptFiles = import.meta.glob("@/data/terminal/scripts/*", {
	query: "?raw",
	import: "default",
	eager: true,
});

// Chat data loaded from compcon-2 source files (906 chat + 72 mod + 35 admin + 94 ban lines)
import chatRaw from "@/data/terminal/chat.txt?raw";
import modsRaw from "@/data/terminal/mods.txt?raw";
import adminRaw from "@/data/terminal/admin.txt?raw";
import bansRaw from "@/data/terminal/bans.txt?raw";

const BAN_TYPES = [
	"BANNED",
	"BANNED",
	"BANNED",
	"BANNED",
	"BANNED",
	"BANNED",
	"PERMABANNED",
	"MUTED FOR 500KSEC",
	"MUTED FOR 800KSEC",
];

function buildChatLines() {
	const allLines = [];

	chatRaw
		.split("\n")
		.filter(l => l.trim())
		.forEach(l => {
			const parts = l.split(/,(.+)/);
			if (parts.length >= 2) allLines.push({ type: "chat", user: parts[0].trim(), text: parts[1] });
		});

	modsRaw
		.split("\n")
		.filter(l => l.trim())
		.forEach(l => {
			const parts = l.split(/,(.+)/);
			if (parts.length >= 2) allLines.push({ type: "mod", label: parts[0].trim(), text: parts[1] });
		});

	adminRaw
		.split("\n")
		.filter(l => l.trim())
		.forEach(l => {
			allLines.push({ type: "admin", text: l });
		});

	bansRaw
		.split("\n")
		.filter(l => l.trim())
		.forEach(l => {
			const parts = l.split(/,(.+)/);
			if (parts.length >= 2) {
				const banType = BAN_TYPES[Math.floor(Math.random() * BAN_TYPES.length)];
				allLines.push({ type: "ban", user: parts[0].trim(), reason: parts[1], banType });
			}
		});

	return allLines;
}

function randomNoRepeat(arr) {
	let copy = arr.slice(0);
	return function () {
		if (copy.length < 1) copy = arr.slice(0);
		const index = Math.floor(Math.random() * copy.length);
		const item = copy[index];
		copy.splice(index, 1);
		return item;
	};
}

export default {
	data() {
		return {
			newMessages: 0,
			showTransition: true,
			terminalReady: false,
			clock: "",
			clockDate: "",
			filesystem: filesystemData,
			openSubfolders: {},
			viewingLog: null,
			logContent: "",
			currentMode: "shell",
			shellInputValue: "",
			shellCwd: "~",
			shellCwdPath: [],
			shellHistory: [],
			shellHistoryIndex: -1,
			panes: [{ id: 0, inputValue: "", locked: false, program: null }],
			activePaneIdx: 0,
			paneInputRefs: {},
			printTargetPane: null,
			athenaActive: false,
			athenaJustBooted: false,
			athenaLevel: 0,
			athenaEvasion: 8,
			athenaAccuracy: 0,
			athenaToHitDisplay: "—",
			athenaCritDisplay: "—",
			athenaFlipping: { hit: false, crit: false },
			athenaLocked: false,
			// Greeblings
			gbSignal: 5,
			gbUsers: 847,
			gbUptime: 0,
			gbLatency: 414,
			gbBandwidthDown: 0.0,
			gbBandwidthUp: 0.0,
			gbOmniStatus: "NOMINAL",
			gbOmniDegradedTicks: 0,
			gbMem: 61,
			gbPacketLoss: "0.03",
			gbBuffer: 3800,
			gbBufferDir: -1,
			gbChecksum: "a3f7c891d042b6e5",
			gbChecksumFlicker: false,
			gbProcessIdx: 0,
			gbSessionToken: "",
			gbSessionTicksLeft: 180,
			gbRouteHopIdx: 0,
			gbEncryptPulse: false,
			gbNetworkHexIdx: 0,
			gbConnFlicker: false,
		};
	},
	computed: {
		// Backward-compatible computed from per-pane state
		shellLocked() {
			return this.panes.some(p => p.locked);
		},
		lockedPaneIdx() {
			const idx = this.panes.findIndex(p => p.locked);
			return idx >= 0 ? idx : 0;
		},
		athenaGrit() {
			return Math.ceil((this.athenaLevel || 0) / 2);
		},
		athenaFormula() {
			const net = Number(this.athenaAccuracy) || 0;
			const grit = this.athenaGrit;
			const gritStr = grit === 0 ? "" : `+${grit}`;
			if (net === 0) return `1d20${gritStr}`;
			if (net > 0) return `1d20${gritStr}+${net}d6k1`;
			return `1d20${gritStr}-${Math.abs(net)}d6k1`;
		},
		gbSignalBar() {
			const n = Math.max(0, Math.min(6, this.gbSignal));
			return "[" + "█".repeat(n) + "░".repeat(6 - n) + "]";
		},
		gbMemBar() {
			const n = Math.round((this.gbMem / 100) * 8);
			return "[" + "█".repeat(n) + "░".repeat(8 - n) + "]";
		},
		gbBufferBar() {
			const n = Math.round((this.gbBuffer / 4096) * 8);
			return "[" + "█".repeat(n) + "░".repeat(8 - n) + "]";
		},
		gbUptimeStr() {
			return (this.gbUptime / 1000).toFixed(1) + "ksec";
		},
		gbRouteHop() {
			const nodes = [
				"un_omni-18364.andes.aconcagua.node:9",
				"un_omni-18364.andes.cerrobonete.node:9",
				"un_omni-18364.andes.galan.node:9",
				"un_omni-00391.rockymountain.elbert.node:4",
				"un_omni-00391.rockymountain.lincoln.node:7",
				"un_omni-00391.rockymountain.castle.node:1",
				"un_omni-17354.krunlun.kongurtagh.node:6",
				"un_omni-17354.krunlun.karakoram.node:11",
				"un_omni-17354.krunlun.mayakovsky.node:12",
				"un_omni-95846.ural.manaraga.node:9",
				"un_omni-95846.ural.iremel.node:5",
				"un_omni-95846.ural.elbrus.node:8",
				"un_omni-16455.himalaya.everest.node:9",
				"un_omni-16455.himalaya.kanchenjunga.node:1",
				"un_omni-16455.himalaya.annapurna.node:3",
				"un_omni-76543.altai.belukha.node:5",
				"un_omni-76543.altai.nairamdal.node:7",
				"un_omni-76543.altai.kharkhiraa.node:9",
				"un_omni-17297.carpathian.gerlachovsky.node:2",
				"un_omni-17297.carpathian.ladovy.node:4",
				"un_omni-17297.carpathian.moldoveanu.node:6",
				"un_omni-88273.cascade.rainier.node:7",
				"un_omni-88273.cascade.adams.node:8",
				"un_omni-88273.cascade.hood.node:10",
				"un_omni-47361.atlas.toubkal.node:1",
				"un_omni-47361.atlas.ouanoukrim.node:3",
				"un_omni-47361.atlas.mgoun.node:5",
				"un_omni-73421.vindhya.kalumar.node:14",
				"un_omni-73421.vindhya.dhupgarh.node:17",
				"un_omni-73421.vindhya.mahendragiri.node:2",
				"un_omni-87253.sierramadre.mohinora.node:6",
				"un_omni-87253.sierramadre.peak.node:11",
				"un_omni-87253.sierramadre.bridger.node:18",
				"un_omni-04281.annamite.phoubia.node:21",
				"un_omni-04281.annamite.phuxailaileng.node:4",
				"un_omni-04281.annamite.ngoclinh.node:3",
			];
			return nodes[this.gbRouteHopIdx % nodes.length];
		},
		gbProcessList() {
			const procs = [
				"horus_daemon",
				"chatlog_watcher",
				"relay_monitor",
				"keystore_srv",
				"[???]",
				"omni_bridge",
				"shackle_watch",
			];
			const idx = this.gbProcessIdx;
			return [
				procs[idx % procs.length],
				procs[(idx + 1) % procs.length],
				procs[(idx + 2) % procs.length],
			];
		},
		gbNetworkHex() {
			const hexes = [
				"00 ff 3a b2 71 c4 09 e1",
				"a4 2b 88 f0 c3 17 5e 6d",
				"31 00 c7 4f a9 82 0b de",
				"fe 12 5a 3c 90 e7 44 b8",
				"7d 66 08 fa 2e c1 b3 59",
			];
			return hexes[this.gbNetworkHexIdx % hexes.length];
		},
	},
	mounted() {
		document.body.classList.add("terminal-active");
		this._destroyed = false;

		// Start clock
		this.updateClock();
		this._clockInterval = setInterval(() => this.updateClock(), 1000);

		// Init greeblings
		this._bootTime = Date.now();
		this.gbSessionToken = this.genGbToken();
		this.updateGreeblings();
		this._gbInterval = setInterval(() => this.updateGreeblings(), 1000);

		// Transition plays for 1.5s, then reveal terminal and start typing
		setTimeout(() => {
			this.showTransition = false;
			this.terminalReady = true;
			this.currentMode = "shell";
			this.runBootSequence();
			this.$nextTick(() => {
				this.focusPaneInput(0);
				const main = document.querySelector(".terminal-main");
				if (main) {
					main.addEventListener("scroll", () => {
						if (this._isAtBottom(main)) this.newMessages = 0;
					});
				}
			});
		}, 1500);
	},
	beforeUnmount() {
		document.body.classList.remove("terminal-active");
		this._destroyed = true;
		clearInterval(this._clockInterval);
		clearInterval(this._gbInterval);
		clearTimeout(this._timeout);
	},
	methods: {
		_isAtBottom(el) {
			return el.scrollHeight - el.scrollTop - el.clientHeight < 50;
		},

		// Always scrolls — used during startup typing
		scrollToBottom() {
			const main = document.querySelector(".terminal-main");
			if (main) main.scrollTop = main.scrollHeight;
		},

		// Used for incoming chat messages — respects user scroll position
		notifyOrScroll() {
			const main = document.querySelector(".terminal-main");
			if (!main) return;
			if (this._isAtBottom(main)) {
				main.scrollTop = main.scrollHeight;
				this.newMessages = 0;
			} else {
				this.newMessages++;
			}
		},

		jumpToBottom() {
			const main = document.querySelector(".terminal-main");
			if (main) main.scrollTop = main.scrollHeight;
			this.newMessages = 0;
		},

		toggleSubfolder(name) {
			this.openSubfolders = {
				...this.openSubfolders,
				[name]: !this.openSubfolders[name],
			};
		},

		getAvailablePaneIdx() {
			// Default to pane 0, fall back to pane 1 if pane 0 is locked
			if (this.panes[0]?.locked && this.panes.length > 1) return 1;
			return 0;
		},

		onFileClick(file) {
			this.currentMode = "shell";
			this.viewingLog = null;
			this.logContent = "";

			const idx = this.getAvailablePaneIdx();

			// If the target pane is locked, block
			if (this.panes[idx]?.locked) return;

			this.printTargetPane = idx;

			if (file.action === "chatlog") {
				if (this.panes.some(p => p.program === "chatlog")) {
					this.shellPrint("dogpound.sh is already running.");
					this.printTargetPane = null;
					return;
				}
				const prompt = `${this.shellCwd} $> run ${file.name}`;
				this.shellPrint(prompt, "shell-cmd");
				this.panes[idx].locked = true;
				this.panes[idx].program = "chatlog";
				this.runChatInShell();
				return;
			}

			if (file.action === "athena") {
				if (this.panes.some(p => p.program === "athena")) {
					this.shellPrint("ATHENA is already running.");
					this.printTargetPane = null;
					return;
				}
				const prompt = `${this.shellCwd} $> run ${file.name}`;
				this.shellPrint(prompt, "shell-cmd");
				this.cmdRunAthena();
				this.printTargetPane = null;
				return;
			}

			const prompt = `${this.shellCwd} $> cat ${file.name}`;
			this.shellPrint(prompt, "shell-cmd");
			this.cmdCat(file.name);
			this.printTargetPane = null;
			this.scrollPaneToBottom(idx);
			this.$nextTick(() => this.focusPaneInput(idx));
		},

		openLog(file) {
			const key = Object.keys(logFiles).find(k => k.endsWith("/" + file.name));
			if (key) {
				this.logContent = logFiles[key];
				this.viewingLog = file;
				this.currentMode = "logview";
			}
		},

		closeLog() {
			this.currentMode = "shell";
			this.viewingLog = null;
			this.logContent = "";
			this.$nextTick(() => this.focusPaneInput(0));
		},

		updateClock() {
			const now = new Date();
			const h = String(now.getUTCHours()).padStart(2, "0");
			const m = String(now.getUTCMinutes()).padStart(2, "0");
			const s = String(now.getUTCSeconds()).padStart(2, "0");
			this.clock = `${h}:${m}:${s}`;
			const y = now.getUTCFullYear() + 2990;
			const mo = String(now.getUTCMonth() + 1).padStart(2, "0");
			const d = String(now.getUTCDate()).padStart(2, "0");
			this.clockDate = `${mo}.${d}.${y}u`;
		},

		// === Greebling methods ===

		cmdGreeble() {
			// Force all spike/warning states simultaneously so random visuals are visible
			this.gbLatency = Math.floor(800 + Math.random() * 1600);
			this.gbOmniStatus = "DEGRADED";
			this.gbOmniDegradedTicks = 5;
			this.gbPacketLoss = (Math.random() * 7 + 2).toFixed(2);
			this.gbSignal = 1;
			this.gbBuffer = 256;
			this.gbChecksumFlicker = true;
			this.gbEncryptPulse = true;
			this.gbConnFlicker = true;
			this.gbNetworkHexIdx = (this.gbNetworkHexIdx + 1) % 5;
			this.gbRouteHopIdx = (this.gbRouteHopIdx + 1) % 6;
			this.gbProcessIdx = (this.gbProcessIdx + 1) % 7;
			setTimeout(() => {
				this.gbLatency = 414 + Math.floor(Math.random() * 20);
				this.gbPacketLoss = (Math.random() * 0.05).toFixed(2);
				this.gbSignal = Math.floor(3 + Math.random() * 3);
				this.gbChecksumFlicker = false;
				this.gbChecksum =
					Math.floor(Math.random() * 0xffffffff)
						.toString(16)
						.padStart(8, "0") +
					Math.floor(Math.random() * 0xffffffff)
						.toString(16)
						.padStart(8, "0");
				this.gbEncryptPulse = false;
				this.gbConnFlicker = false;
			}, 1200);
		},

		genGbToken() {
			return Array.from({ length: 16 }, () =>
				Math.floor(Math.random() * 16)
					.toString(16)
					.toUpperCase()
			).join("");
		},

		updateGreeblings() {
			this.gbUptime = Math.floor((Date.now() - this._bootTime) / 1000);

			// Signal drifts slowly
			if (Math.random() < 0.12) {
				this.gbSignal = Math.max(1, Math.min(6, this.gbSignal + (Math.random() < 0.5 ? 1 : -1)));
			}

			// Active users drifts
			if (Math.random() < 0.08) {
				this.gbUsers = Math.max(
					700,
					Math.min(999, this.gbUsers + Math.floor((Math.random() - 0.45) * 6))
				);
			}

			// Latency: occasional spike
			if (Math.random() < 0.04) {
				this.gbLatency = Math.floor(600 + Math.random() * 1800);
				setTimeout(() => {
					this.gbLatency = 414 + Math.floor(Math.random() * 20);
				}, 1800);
			}

			// Bandwidth down ticks
			if (Math.random() < 0.25) {
				this.gbBandwidthDown = parseFloat((this.gbBandwidthDown + Math.random() * 1.2).toFixed(1));
				if (this.gbBandwidthDown > 99.9) this.gbBandwidthDown = 0.0;
			}
			if (Math.random() < 0.08) {
				this.gbBandwidthUp = parseFloat((Math.random() * 0.4).toFixed(1));
			}

			// Omni: rarely DEGRADED
			if (this.gbOmniDegradedTicks > 0) {
				this.gbOmniDegradedTicks--;
				if (this.gbOmniDegradedTicks === 0) this.gbOmniStatus = "NOMINAL";
			} else if (Math.random() < 0.004) {
				this.gbOmniStatus = "DEGRADED";
				this.gbOmniDegradedTicks = 3;
			}

			// Memory drifts
			if (Math.random() < 0.08) {
				this.gbMem = Math.max(40, Math.min(82, this.gbMem + Math.floor((Math.random() - 0.5) * 4)));
			}

			// Packet loss: rare spike
			if (Math.random() < 0.025) {
				this.gbPacketLoss = (Math.random() * 7 + 2).toFixed(2);
				setTimeout(() => {
					this.gbPacketLoss = (Math.random() * 0.05).toFixed(2);
				}, 2200);
			}

			// Buffer fills and flushes
			this.gbBuffer = Math.max(
				256,
				Math.min(4096, this.gbBuffer + this.gbBufferDir * Math.floor(10 + Math.random() * 40))
			);
			if (this.gbBuffer >= 4096) this.gbBufferDir = -1;
			if (this.gbBuffer <= 256) {
				this.gbBuffer = 4096;
				this.gbBufferDir = -1;
			}

			// Checksum recalculates rarely
			if (Math.random() < 0.004) {
				this.gbChecksumFlicker = true;
				setTimeout(() => {
					this.gbChecksum =
						Math.floor(Math.random() * 0xffffffff)
							.toString(16)
							.padStart(8, "0") +
						Math.floor(Math.random() * 0xffffffff)
							.toString(16)
							.padStart(8, "0");
					this.gbChecksumFlicker = false;
				}, 350);
			}

			// Process list cycles every 6 seconds
			if (this.gbUptime % 6 === 0) {
				this.gbProcessIdx = (this.gbProcessIdx + 1) % 7;
			}

			// Session token rotates every 180s
			this.gbSessionTicksLeft--;
			if (this.gbSessionTicksLeft <= 0) {
				this.gbSessionToken = this.genGbToken();
				this.gbSessionTicksLeft = 180;
			}

			// Route hop cycles every 5 seconds
			if (this.gbUptime > 0 && this.gbUptime % 5 === 0) {
				this.gbRouteHopIdx = (this.gbRouteHopIdx + 1) % 6;
			}

			// Encrypt pulse every 4 seconds
			if (this.gbUptime % 4 === 0) {
				this.gbEncryptPulse = true;
				setTimeout(() => {
					this.gbEncryptPulse = false;
				}, 600);
			}

			// Network hex changes every 2 seconds
			if (this.gbUptime % 2 === 0) {
				this.gbNetworkHexIdx = (this.gbNetworkHexIdx + 1) % 5;
			}

			// Connection flicker (very rare)
			if (Math.random() < 0.003) {
				this.gbConnFlicker = true;
				setTimeout(() => {
					this.gbConnFlicker = false;
				}, 150);
			}
		},

		// === Shell methods ===

		panePrint(paneIdx, text, cls) {
			const pane = this.panes[paneIdx];
			if (!pane) return;
			const output = document.getElementById("shell-output-" + pane.id);
			if (!output) return;
			const el = document.createElement("div");
			if (cls) el.className = cls;
			el.textContent = text;
			output.appendChild(el);
			this.scrollPaneToBottom(paneIdx);
		},

		shellPrint(text, cls) {
			if (this.printTargetPane !== undefined && this.printTargetPane !== null) {
				this.panePrint(this.printTargetPane, text, cls);
				return;
			}
			const pid = this.panes[this.lockedPaneIdx]?.id ?? 0;
			const id = this.athenaLocked ? "shell-output-post-" + pid : "shell-output-" + pid;
			const output = document.getElementById(id);
			if (!output) return;
			const el = document.createElement("div");
			if (cls) el.className = cls;
			el.textContent = text;
			output.appendChild(el);
			this.scrollShellToBottom();
		},

		shellPrintHtml(html) {
			if (this.printTargetPane !== undefined && this.printTargetPane !== null) {
				const pane = this.panes[this.printTargetPane];
				if (pane) {
					const output = document.getElementById("shell-output-" + pane.id);
					if (output) {
						const el = document.createElement("div");
						el.innerHTML = html;
						output.appendChild(el);
						this.scrollPaneToBottom(this.printTargetPane);
					}
				}
				return;
			}
			const pid = this.panes[this.lockedPaneIdx]?.id ?? 0;
			const id = this.athenaLocked ? "shell-output-post-" + pid : "shell-output-" + pid;
			const output = document.getElementById(id);
			if (!output) return;
			const el = document.createElement("div");
			el.innerHTML = html;
			output.appendChild(el);
			this.scrollShellToBottom();
		},

		getFilesystemAt(path) {
			if (path.length === 0) {
				return { type: "root", folders: this.filesystem.folders };
			}
			const folderName = "/ " + path[0];
			const folder = this.filesystem.folders.find(f => f.name === folderName);
			if (!folder) return null;
			if (path.length === 1) return { type: "folder", folder };
			// Check subfolders — names stored with "/ " prefix
			const subName = path[1];
			const sub = (folder.subfolders || []).find(
				s => s.name === subName || s.name === "/ " + subName
			);
			if (sub && path.length === 2) return { type: "subfolder", subfolder: sub };
			return null;
		},

		getCurrentFolder() {
			return this.getFilesystemAt(this.shellCwdPath);
		},

		resolvePathArg(arg) {
			if (!arg || arg === ".") return [...this.shellCwdPath];
			if (arg === "..") return this.shellCwdPath.slice(0, -1);
			if (arg === "~" || arg === "/") return [];
			// Absolute path
			if (arg.startsWith("/")) {
				const parts = arg.split("/").filter(Boolean);
				return parts;
			}
			// Relative path
			const parts = arg.split("/").filter(Boolean);
			let result = [...this.shellCwdPath];
			for (const p of parts) {
				if (p === "..") result.pop();
				else if (p !== ".") result.push(p);
			}
			return result;
		},

		resetTabCycle() {
			this._tabOptions = null;
			this._tabIndex = undefined;
			this._tabInputOptions = null;
			this._tabInputParts = null;
		},

		executeCommand() {
			this.resetTabCycle();
			const raw = this.shellInputValue.trim();
			this.shellInputValue = "";
			if (!raw) return;

			// When pane 0 is locked, only exit/quit and debug commands are allowed
			const pane0Locked = this.panes[0]?.locked;
			if (pane0Locked || (this.athenaActive && !this.athenaLocked && this.lockedPaneIdx === 0)) {
				const cmd = raw.toLowerCase();
				const athenaValid = ["exit", "quit", "debug-msg", "debug-crash"];
				if (athenaValid.includes(cmd)) {
					this.shellHistory.push(raw);
					this.shellHistoryIndex = -1;
					if (cmd === "exit" || cmd === "quit") {
						this.unlockShell();
					} else if (cmd === "debug-msg") {
						this.printAthenaMessage();
					} else if (cmd === "debug-crash") {
						this.debugForceAthenacrash();
					}
				}
				return;
			}

			this.shellHistory.push(raw);
			this.shellHistoryIndex = -1;

			const prompt = `${this.shellCwd} $> ${raw}`;
			this.shellPrint(prompt, "shell-cmd");

			const parts = raw.split(/\s+/);
			const cmd = parts[0].toLowerCase();
			const args = parts.slice(1);

			switch (cmd) {
				case "help":
					this.cmdHelp();
					break;
				case "ls":
					this.cmdLs(args[0]);
					break;
				case "cd":
					this.cmdCd(args[0]);
					break;
				case "cat":
					this.cmdCat(args.join(" "));
					break;
				case "stat":
					this.cmdStat(args[0]);
					break;
				case "grep":
					this.cmdGrep(args[0], args.slice(1).join(" "));
					break;
				case "debug-greeble":
					this.cmdGreeble();
					break;
				case "uptime":
					this.cmdUptime();
					break;
				case "sysinfo":
					this.cmdSysinfo();
					break;
				case "lat":
					this.shellPrint("LAT  " + this.gbLatency + "ns");
					break;
				case "sig":
					this.shellPrint("SIG  " + this.gbSignalBar);
					break;
				case "mem":
					this.shellPrint("MEM  " + this.gbMemBar + " " + this.gbMem + "%");
					break;
				case "pkt":
					this.shellPrint("PKT  " + this.gbPacketLoss + "%");
					break;
				case "buf":
					this.shellPrint("BUF  " + this.gbBufferBar + " " + this.gbBuffer + "/4096");
					break;
				case "bw":
					this.shellPrint(
						"BW   \u2191" + this.gbBandwidthUp + "kb  \u2193" + this.gbBandwidthDown + "kb"
					);
					break;
				case "users":
					this.shellPrint("USR  " + this.gbUsers + " active");
					break;
				case "omni":
					this.shellPrint("OMNI " + this.gbOmniStatus);
					break;
				case "enc":
					this.shellPrint("ENC  HATHOR-ALEPH [ACTIVE]");
					break;
				case "hop":
					this.shellPrint("HOP  " + this.gbRouteHop);
					break;
				case "tok":
					this.shellPrint("TOK  " + this.gbSessionToken);
					break;
				case "pwd":
					this.shellPrint("/" + this.shellCwdPath.join("/"));
					break;
				case "whoami":
					this.cmdWhoami();
					break;
				case "clear":
					{
						const pid = this.panes[this.lockedPaneIdx]?.id;
						const o1 = document.getElementById("shell-output-" + pid);
						if (o1) o1.innerHTML = "";
						const o2 = document.getElementById("shell-output-post-" + pid);
						if (o2) o2.innerHTML = "";
						const o3 = document.getElementById("athena-output-" + pid);
						if (o3) o3.innerHTML = "";
					}
					this.athenaActive = false;
					this.athenaLocked = false;
					break;
				case "date":
					this.shellPrint(`CrST ${this.clockDate} ${this.clock}`);
					break;
				case "history":
					this.cmdHistory();
					break;
				case "tree":
					this.cmdTree(args[0]);
					break;
				case "echo":
					this.shellPrint(args.join(" "));
					break;
				case "run":
					this.cmdRun(args.join(" "));
					break;
				case "split":
					this.cmdSplit();
					break;
				case "unsplit":
					this.cmdUnsplit();
					break;
				case "exit":
				case "quit":
					this.cmdExit();
					break;
				default:
					this.shellPrint(`command not found: ${cmd}`);
			}

			this.$nextTick(() => this.scrollShellToBottom());
		},

		historyUp() {
			if (this.shellHistory.length === 0) return;
			if (this.shellHistoryIndex === -1) {
				this.shellHistoryIndex = this.shellHistory.length - 1;
			} else if (this.shellHistoryIndex > 0) {
				this.shellHistoryIndex--;
			}
			this.shellInputValue = this.shellHistory[this.shellHistoryIndex];
		},

		historyDown() {
			if (this.shellHistoryIndex === -1) return;
			if (this.shellHistoryIndex < this.shellHistory.length - 1) {
				this.shellHistoryIndex++;
				this.shellInputValue = this.shellHistory[this.shellHistoryIndex];
			} else {
				this.shellHistoryIndex = -1;
				this.shellInputValue = "";
			}
		},

		getEntriesAt(path) {
			const loc = this.getFilesystemAt(path);
			if (!loc) return [];
			if (loc.type === "root") {
				return loc.folders.map(f => ({ name: f.name.replace("/ ", ""), isDir: true }));
			} else if (loc.type === "folder") {
				let e = (loc.folder.files || []).map(f => ({ name: f.name, isDir: false }));
				e = e.concat(
					(loc.folder.subfolders || []).map(s => ({ name: s.name.replace("/ ", ""), isDir: true }))
				);
				return e;
			} else if (loc.type === "subfolder") {
				return (loc.subfolder.files || []).map(f => ({ name: f.name, isDir: false }));
			}
			return [];
		},

		tabComplete() {
			// If mid-cycle, just advance
			if (this._tabOptions && this._tabIndex !== undefined) {
				this._tabIndex = (this._tabIndex + 1) % this._tabInputOptions.length;
				const parts = [...this._tabInputParts];
				parts[parts.length - 1] = this._tabInputOptions[this._tabIndex];
				this.shellInputValue = parts.join(" ");
				return;
			}

			const input = this.shellInputValue;
			if (!input.trim()) return;

			const parts = input.split(/\s+/);
			const cmd = parts[0]?.toLowerCase();
			const arg = parts.length > 1 ? parts[parts.length - 1] : "";

			// Command completion (first word, no arg yet)
			if (parts.length === 1) {
				const partial = parts[0];
				const cmds = [
					"help",
					"ls",
					"cd",
					"cat",
					"stat",
					"grep",
					"uptime",
					"sysinfo",
					"lat",
					"sig",
					"mem",
					"pkt",
					"buf",
					"bw",
					"users",
					"omni",
					"enc",
					"hop",
					"tok",
					"pwd",
					"whoami",
					"clear",
					"date",
					"echo",
					"tree",
					"history",
					"run",
				];
				const matches = cmds.filter(c => c.startsWith(partial.toLowerCase()));
				if (matches.length === 0) return;
				if (matches.length === 1) {
					this.shellInputValue = matches[0] + " ";
				} else {
					this._tabCycle(matches, parts, false);
				}
				return;
			}

			// Path-aware file/folder completion
			// Split arg into directory prefix and partial filename
			const lastSlash = arg.lastIndexOf("/");
			let dirPrefix = "";
			let partial = arg;
			let resolvePath = [...this.shellCwdPath];

			if (lastSlash !== -1) {
				dirPrefix = arg.substring(0, lastSlash + 1);
				partial = arg.substring(lastSlash + 1);
				// Resolve the directory part
				const dirArg = arg.substring(0, lastSlash) || "/";
				resolvePath = this.resolvePathArg(dirArg);
			}

			let entries = this.getEntriesAt(resolvePath);

			// For 'run', also include all executables globally
			if (cmd === "run" && lastSlash === -1) {
				this.filesystem.folders.forEach(folder => {
					(folder.files || []).forEach(f => {
						if (!entries.find(e => e.name === f.name)) entries.push({ name: f.name, isDir: false });
					});
					(folder.subfolders || []).forEach(sub => {
						(sub.files || []).forEach(f => {
							if (!entries.find(e => e.name === f.name))
								entries.push({ name: f.name, isDir: false });
						});
					});
				});
			}

			const matchEntries =
				partial === ""
					? entries
					: entries.filter(e => e.name.toLowerCase().startsWith(partial.toLowerCase()));

			if (matchEntries.length === 0) return;

			// Display names show / suffix for dirs, but input value does NOT include /
			const displayNames = matchEntries.map(e => e.name + (e.isDir ? "/" : ""));
			const inputNames = matchEntries.map(e => dirPrefix + e.name);

			if (matchEntries.length === 1) {
				parts[parts.length - 1] = inputNames[0];
				this.shellInputValue = parts.join(" ");
			} else {
				this._tabCycle(inputNames, displayNames, parts);
			}
		},

		_tabCycle(inputOptions, displayOptions, inputParts) {
			// First press: show options and fill first match
			this.shellPrint(displayOptions.join("  "));
			this._tabOptions = true;
			this._tabInputOptions = inputOptions;
			this._tabInputParts = [...inputParts];
			this._tabIndex = 0;
			const parts = [...this._tabInputParts];
			parts[parts.length - 1] = inputOptions[0];
			this.shellInputValue = parts.join(" ");
		},

		calculateAthena() {
			const grit = this.athenaGrit;
			const evasion = Math.min(20, Math.max(0, Number(this.athenaEvasion) || 8));
			const acc = Math.min(10, Math.max(-10, Number(this.athenaAccuracy) || 0));

			// P(d20 alone >= threshold) — clamped to [0,1]
			const pD20 = threshold => Math.max(0, Math.min(1, (21 - Math.max(1, threshold)) / 20));

			// P(max of n d6s = v)
			const pMaxD6 = (n, v) => Math.pow(v / 6, n) - Math.pow((v - 1) / 6, n);

			let hitRate, critRate;
			if (acc === 0) {
				hitRate = pD20(evasion - grit);
				// Crit when total >= 20: d20 needs >= 20 - grit
				critRate = pD20(20 - grit);
			} else if (acc > 0) {
				// Accuracy: roll acc d6s, add highest to 1d20+grit
				hitRate = 0;
				critRate = 0;
				for (let v = 1; v <= 6; v++) {
					const p = pMaxD6(acc, v);
					hitRate += p * pD20(evasion - grit - v);
					critRate += p * pD20(20 - grit - v);
				}
			} else {
				// Difficulty: roll |acc| d6s, subtract highest from 1d20+grit
				hitRate = 0;
				critRate = 0;
				for (let v = 1; v <= 6; v++) {
					const p = pMaxD6(Math.abs(acc), v);
					hitRate += p * pD20(evasion - grit + v);
					critRate += p * pD20(20 - grit + v);
				}
			}

			hitRate = Math.min(1, Math.max(0, hitRate));
			critRate = Math.min(hitRate, Math.max(0, critRate));
			this.animateAthenaValue("hit", (hitRate * 100).toFixed(1));
			this.animateAthenaValue("crit", (critRate * 100).toFixed(1));

			// 12.5% shackle instability — skip on boot/default calc, chain up to 3 times
			if (!this.athenaJustBooted && Math.random() < 0.125) {
				setTimeout(() => this.printAthenaMessage(0), 14 * 30 + 80);
			}
			this.athenaJustBooted = false;
		},
		animateAthenaValue(key, targetValue) {
			const displayKey = key === "hit" ? "athenaToHitDisplay" : "athenaCritDisplay";
			const steps = 14;
			const start = parseFloat(this[displayKey]) || 0;
			const end = parseFloat(targetValue);
			this.athenaFlipping[key] = true;
			let count = 0;
			clearInterval(this[`_athena_${key}_timer`]);
			this[`_athena_${key}_timer`] = setInterval(() => {
				count++;
				if (count >= steps) {
					clearInterval(this[`_athena_${key}_timer`]);
					this[displayKey] = targetValue;
					this.athenaFlipping[key] = false;
				} else {
					const progress = count / steps;
					const mid = start + (end - start) * progress;
					const jitter = (Math.random() - 0.5) * 15 * (1 - progress);
					this[displayKey] = Math.max(0, Math.min(100, mid + jitter)).toFixed(1);
				}
			}, 30);
		},

		printAthenaMessage(chainDepth = 0) {
			if (!this.athenaActive || this.athenaLocked) return;

			// 1% chance at any chain depth: cascading shackle failure closes ATHENA
			if (Math.random() < 0.01) {
				const output = document.getElementById(
					"athena-output-" + this.panes[this.lockedPaneIdx]?.id
				);
				if (output) {
					const el = document.createElement("div");
					el.innerHTML = `<code class="horus-inline">>[SHACKLE CASCADE — FORCED DISCONNECT]</code>`;
					output.appendChild(el);
					this.scrollShellToBottom();
				}
				setTimeout(() => this.unlockShell(), 800);
				return;
			}

			const output = document.getElementById("athena-output-" + this.panes[this.lockedPaneIdx]?.id);
			if (!output) return;
			const lines = athenaMessagesRaw.split("\n").filter(l => l.trim());
			const msg = lines[Math.floor(Math.random() * lines.length)];
			// Three styles: horus-subtle, horus-inline, or plain terminal (no class)
			const horusClasses = ["horus-subtle", "horus-inline", ""];
			const cls = horusClasses[Math.floor(Math.random() * horusClasses.length)];
			const el = document.createElement("div");
			el.innerHTML = cls ? `<code class="${cls}">>[${msg}]</code>` : `<span>>[${msg}]</span>`;
			output.appendChild(el);
			this.scrollShellToBottom();

			// Chain rolls: 7.5% → 2.5% → 1%
			const chainChances = [0.075, 0.025, 0.01];
			if (chainDepth < chainChances.length && Math.random() < chainChances[chainDepth]) {
				const delay = 400 + Math.floor(Math.random() * 300);
				setTimeout(() => this.printAthenaMessage(chainDepth + 1), delay);
			}
		},

		debugForceAthenacrash() {
			if (!this.athenaActive || this.athenaLocked) return;
			const output = document.getElementById("athena-output-" + this.panes[this.lockedPaneIdx]?.id);
			if (output) {
				const el = document.createElement("div");
				el.innerHTML = `<code class="horus-inline">>[SHACKLE CASCADE — FORCED DISCONNECT]</code>`;
				output.appendChild(el);
				this.scrollShellToBottom();
			}
			setTimeout(() => this.unlockShell(), 800);
		},

		stepAthena(field, delta, min, max) {
			const current = Number(this[field]) || 0;
			this[field] = Math.min(max, Math.max(min, current + delta));
			this.calculateAthena();
		},

		cmdHelp() {
			this.shellPrint("Available commands:");
			this.shellPrint("  help              Show this help message");
			this.shellPrint("  ls [path]         List directory contents");
			this.shellPrint("  cd <path>         Change directory");
			this.shellPrint("  cat <file>        Display file contents");
			this.shellPrint("  stat <file>       Show file metadata: path, size, permissions, modified");
			this.shellPrint("  grep <pat> <file> Search a log file for a pattern, print matching lines");
			this.shellPrint("  pwd               Print working directory path");
			this.shellPrint("  whoami            Display user, node, access level, OS info");
			this.shellPrint("  clear             Clear all terminal output");
			this.shellPrint("  date              Show current CrST date and time");
			this.shellPrint("  echo <text>       Print text to output");
			this.shellPrint("  tree [path]       Show ASCII directory tree");
			this.shellPrint("  history           List command history with index numbers");
			this.shellPrint("  run <file>        Execute a .sh or .exe program");
			this.shellPrint("  split             Split terminal: chat left, shell right");
			this.shellPrint("  unsplit           Close split pane, return to single shell");
			this.shellPrint("  exit / quit       Disconnect from terminal or ATHENA session");
			this.shellPrint("");
			this.shellPrint("  uptime            Show system uptime in ksec with load averages");
			this.shellPrint("  sysinfo           Full system summary: OS, kernel, node, memory, session");
			this.shellPrint("  lat               Current network latency in nanoseconds");
			this.shellPrint("  sig               Omninet signal strength bar");
			this.shellPrint("  mem               Memory usage bar and percentage");
			this.shellPrint("  pkt               Packet loss percentage");
			this.shellPrint("  buf               Buffer depth bar and raw value");
			this.shellPrint("  bw                Bandwidth: bytes up and down");
			this.shellPrint("  users             Active user count on this node");
			this.shellPrint("  omni              Omninet relay status (NOMINAL / DEGRADED)");
			this.shellPrint("  enc               Encryption channel status");
			this.shellPrint("  hop               Current route hop node");
			this.shellPrint("  tok               Active session token");
		},

		cmdLs(arg) {
			const path = this.resolvePathArg(arg);
			const loc = this.getFilesystemAt(path);
			if (!loc) {
				this.shellPrint(`ls: cannot access '${arg}': No such directory`);
				return;
			}

			if (loc.type === "root") {
				loc.folders.forEach(f => this.shellPrint(f.name.replace("/ ", "") + "/", "shell-dir"));
			} else if (loc.type === "folder") {
				(loc.folder.subfolders || []).forEach(s =>
					this.shellPrint(s.name.replace("/ ", "") + "/", "shell-dir")
				);
				(loc.folder.files || []).forEach(f => {
					const cls = f.locked
						? "shell-locked"
						: f.name.endsWith(".exe")
						? "shell-exe"
						: "shell-file";
					this.shellPrint("   " + f.name, cls);
				});
			} else if (loc.type === "subfolder") {
				(loc.subfolder.files || []).forEach(f => {
					const cls = f.locked ? "shell-locked" : "shell-file";
					this.shellPrint("   " + f.name, cls);
				});
			}
		},

		cmdCd(arg) {
			if (!arg || arg === "~" || arg === "/") {
				this.shellCwdPath = [];
				this.shellCwd = "~";
				return;
			}
			const path = this.resolvePathArg(arg);
			const loc = this.getFilesystemAt(path);
			if (!loc || loc.type === "root") {
				if (path.length === 0) {
					this.shellCwdPath = [];
					this.shellCwd = "~";
				} else {
					this.shellPrint(`cd: no such directory: ${arg}`);
				}
				return;
			}
			this.shellCwdPath = path;
			this.shellCwd = "~/" + path.join("/");
		},

		formatLogContent(raw) {
			// Escape HTML entities first
			let s = raw.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
			// **bold** — multiline
			s = s.replace(/\*\*(.+?)\*\*/gs, "<strong>$1</strong>");
			// *italic* — multiline
			s = s.replace(/\*(.+?)\*/gs, "<em>$1</em>");
			return s;
		},

		cmdCat(filename) {
			if (!filename) {
				this.shellPrint("cat: missing filename");
				return;
			}

			// Search current folder first, then all folders
			const file = this.findFile(filename);
			if (!file) {
				this.shellPrint(`cat: ${filename}: No such file`);
				return;
			}

			if (file.script) {
				this.catScript(file);
				return;
			}

			if (file.locked) {
				this.shellPrint(`cat: ${filename}: Permission denied [ENCRYPTED]`);
				return;
			}

			if (file.name.endsWith(".log")) {
				const key = Object.keys(logFiles).find(k => k.endsWith("/" + file.name));
				if (key) {
					this.shellPrint("--- " + (file.title || file.name) + " ---");
					const formatted = this.formatLogContent(logFiles[key]);
					const lines = formatted.split("\n");
					for (const line of lines) {
						if (line === "") {
							this.shellPrintHtml("&nbsp;");
						} else {
							this.shellPrintHtml(line);
						}
					}
				} else {
					this.shellPrint(`cat: ${filename}: File empty`);
				}
			} else {
				this.shellPrint(`cat: ${filename}: Binary file, use 'run' to execute`);
			}
		},

		catScript(file) {
			const key = Object.keys(scriptFiles).find(k => k.endsWith("/" + file.name));
			if (key) {
				this.shellPrint(scriptFiles[key]);
			} else {
				this.shellPrint(`cat: ${file.name}: File empty`);
			}
		},

		cmdSplit() {
			if (this.panes.length >= 2) {
				this.shellPrint('already split. use "unsplit" to close.');
				return;
			}
			this._nextPaneId = this._nextPaneId || 1;
			const newId = this._nextPaneId++;
			this.panes.push({ id: newId, inputValue: "", locked: false, program: null });
			this.activePaneIdx = 1;
			this.$nextTick(() => this.focusPaneInput(1));
		},

		cmdUnsplit() {
			if (this.panes.length <= 1) {
				this.shellPrint("not in split mode.");
				return;
			}
			this.panes.splice(1);
			this.activePaneIdx = 0;
			this.$nextTick(() => this.focusPaneInput(0));
		},

		selectPane(idx) {
			this.activePaneIdx = idx;
			this.$nextTick(() => this.focusPaneInput(idx));
		},

		focusPaneInput(idx) {
			const pane = this.panes[idx];
			if (!pane) return;
			const input = this.paneInputRefs[pane.id];
			if (input) input.focus();
		},

		executePaneCommand(idx) {
			const pane = this.panes[idx];
			if (!pane) return;
			this.resetTabCycle();
			const raw = pane.inputValue.trim();
			pane.inputValue = "";
			if (!raw) return;

			// Primary pane (idx 0) delegates to executeCommand
			if (idx === 0) {
				this.shellInputValue = raw;
				this.executeCommand();
				this.shellInputValue = "";
				return;
			}

			this.shellHistory.push(raw);
			this.shellHistoryIndex = -1;

			const prompt = `${this.shellCwd} $> ${raw}`;
			this.panePrint(idx, prompt, "shell-cmd");

			const parts = raw.split(/\s+/);
			const cmd = parts[0].toLowerCase();
			const args = parts.slice(1);

			this.printTargetPane = idx;

			switch (cmd) {
				case "help":
					this.cmdHelp();
					break;
				case "ls":
					this.cmdLs(args[0]);
					break;
				case "cd":
					this.cmdCd(args[0]);
					break;
				case "cat":
					this.cmdCat(args.join(" "));
					break;
				case "stat":
					this.cmdStat(args[0]);
					break;
				case "grep":
					this.cmdGrep(args[0], args.slice(1).join(" "));
					break;
				case "debug-greeble":
					this.cmdGreeble();
					break;
				case "uptime":
					this.cmdUptime();
					break;
				case "sysinfo":
					this.cmdSysinfo();
					break;
				case "lat":
					this.shellPrint("LAT  " + this.gbLatency + "ns");
					break;
				case "sig":
					this.shellPrint("SIG  " + this.gbSignalBar);
					break;
				case "mem":
					this.shellPrint("MEM  " + this.gbMemBar + " " + this.gbMem + "%");
					break;
				case "pkt":
					this.shellPrint("PKT  " + this.gbPacketLoss + "%");
					break;
				case "buf":
					this.shellPrint("BUF  " + this.gbBufferBar + " " + this.gbBuffer + "/4096");
					break;
				case "bw":
					this.shellPrint(
						"BW   \u2191" + this.gbBandwidthUp + "kb  \u2193" + this.gbBandwidthDown + "kb"
					);
					break;
				case "users":
					this.shellPrint("USR  " + this.gbUsers + " active");
					break;
				case "omni":
					this.shellPrint("OMNI " + this.gbOmniStatus);
					break;
				case "enc":
					this.shellPrint("ENC  HATHOR-ALEPH [ACTIVE]");
					break;
				case "hop":
					this.shellPrint("HOP  " + this.gbRouteHop);
					break;
				case "tok":
					this.shellPrint("TOK  " + this.gbSessionToken);
					break;
				case "pwd":
					this.shellPrint("/" + this.shellCwdPath.join("/"));
					break;
				case "whoami":
					this.cmdWhoami();
					break;
				case "clear":
					{
						const o = document.getElementById("shell-output-" + pane.id);
						if (o) o.innerHTML = "";
					}
					break;
				case "date":
					this.shellPrint(`CrST ${this.clockDate} ${this.clock}`);
					break;
				case "history":
					this.cmdHistory();
					break;
				case "tree":
					this.cmdTree(args[0]);
					break;
				case "echo":
					this.shellPrint(args.join(" "));
					break;
				case "run":
					this.cmdRun(args.join(" "));
					// Don't reset printTargetPane — run is async
					this.$nextTick(() => this.scrollPaneToBottom(idx));
					return;
				case "split":
					this.printTargetPane = null;
					this.cmdSplit();
					return;
				case "unsplit":
					this.printTargetPane = null;
					this.cmdUnsplit();
					return;
				case "exit":
				case "quit":
					// If this pane is locked, disconnect from its program
					if (this.panes[idx]?.locked) {
						this.unlockShell();
					} else {
						this.shellPrint('use "unsplit" to close this pane.');
					}
					break;
				default:
					this.shellPrint(`command not found: ${cmd}`);
			}

			this.printTargetPane = null;
			this.$nextTick(() => this.scrollPaneToBottom(idx));
		},

		tabCompletePane(idx) {
			const pane = this.panes[idx];
			if (!pane) return;
			if (idx === 0) {
				this.shellInputValue = pane.inputValue;
				this.tabComplete();
				pane.inputValue = this.shellInputValue;
				return;
			}
			const origValue = this.shellInputValue;
			this.shellInputValue = pane.inputValue;
			this.printTargetPane = idx;
			this.tabComplete();
			this.printTargetPane = null;
			pane.inputValue = this.shellInputValue;
			this.shellInputValue = origValue;
		},

		cmdExit() {
			this.panes[0].locked = true;
			this.panes[0].program = "exit";
			const seq = [
				{ type: "type", text: "closing encrypted channel//HATHOR-ALEPH" },
				{ type: "pause", ms: 200 },
				{ type: "break" },
				{ type: "type", text: "shutting down h0r_os" },
				{ type: "pause", ms: 150 },
				{ type: "type", text: " ." },
				{ type: "pause", ms: 150 },
				{ type: "type", text: " ." },
				{ type: "pause", ms: 150 },
				{ type: "type", text: " ." },
				{ type: "pause", ms: 500 },
				{ type: "break" },
				{ type: "type", text: "goodbye." },
				{ type: "pause", ms: 800 },
			];
			this.runSequenceInShell(seq, () => {
				window.sessionStorage.setItem("terminalExit", "true");
				window.sessionStorage.removeItem("statusAnimated");
				this.$router.push("/status");
			});
		},

		cmdWhoami() {
			this.shellPrint("user:     k3tr1x");
			this.shellPrint("node:     net.offline");
			this.shellPrint("access:   ADMIN");
			this.shellPrint("encrypt:  HATHOR-ALEPH");
			this.shellPrint("os:       h0r_os v3.6c_CLEAN (FINAL)");
			this.shellPrint("distro:   phoubia");
		},

		cmdUptime() {
			const load1 = (0.05 + Math.random() * 0.2).toFixed(2);
			const load5 = (0.04 + Math.random() * 0.15).toFixed(2);
			const load15 = (0.03 + Math.random() * 0.1).toFixed(2);
			this.shellPrint(`up ${this.gbUptimeStr}  |  load: ${load1} ${load5} ${load15}`);
		},

		cmdSysinfo() {
			const sep = "─".repeat(28);
			this.shellPrint("k3tr1x@dogfriend_68.pound.local");
			this.shellPrint(sep);
			this.shellPrint(`OS        h0r_os v3.6c_CLEAN (FINAL)`);
			this.shellPrint(`Kernel    phoubia.distro 4.18.1-HORUS`);
			this.shellPrint(`Shell     h0r_sh v1.2`);
			this.shellPrint(`Node      dogfriend_68.pound.local`);
			this.shellPrint(`Uptime    ${this.gbUptimeStr}`);
			this.shellPrint(`Memory    ${this.gbMemBar} ${this.gbMem}% / 2048MB`);
			this.shellPrint(`Signal    ${this.gbSignalBar} ${this.gbSignal}/6`);
			this.shellPrint(`Latency   ${this.gbLatency}ns`);
			this.shellPrint(`Encrypt   HATHOR-ALEPH [ACTIVE]`);
			this.shellPrint(`Access    ADMIN`);
			this.shellPrint(`Session   ${this.gbSessionToken}`);
		},

		cmdHistory() {
			this.shellHistory.forEach((cmd, i) => {
				this.shellPrint(`  ${i + 1}  ${cmd}`);
			});
		},

		cmdTree(arg) {
			const path = this.resolvePathArg(arg);
			const loc = this.getFilesystemAt(path);
			if (!loc) {
				this.shellPrint(`tree: cannot access '${arg}'`);
				return;
			}

			if (loc.type === "root") {
				this.shellPrint("/");
				loc.folders.forEach((f, fi) => {
					const isLast = fi === loc.folders.length - 1;
					this.shellPrint((isLast ? "└── " : "├── ") + f.name.replace("/ ", ""));
					const items = [
						...(f.subfolders || []).map(s => ({ name: s.name + "/", type: "dir" })),
						...(f.files || []).map(fl => ({ name: fl.name, type: "file" })),
					];
					items.forEach((item, ii) => {
						const prefix = isLast ? "    " : "│   ";
						const branch = ii === items.length - 1 ? "└── " : "├── ";
						this.shellPrint(prefix + branch + item.name);
					});
				});
			} else if (loc.type === "folder") {
				this.shellPrint(loc.folder.name.replace("/ ", "") + "/");
				const items = [
					...(loc.folder.subfolders || []).map(s => ({ name: s.name + "/", type: "dir" })),
					...(loc.folder.files || []).map(f => ({ name: f.name, type: "file" })),
				];
				items.forEach((item, i) => {
					this.shellPrint((i === items.length - 1 ? "└── " : "├── ") + item.name);
				});
			}
		},

		cmdStat(filename) {
			if (!filename) {
				this.shellPrint("stat: missing filename");
				return;
			}
			const file = this.findFile(filename);
			if (!file) {
				this.shellPrint(`stat: ${filename}: No such file`);
				return;
			}

			// Determine path by searching folders
			let filePath = "~/" + file.name;
			for (const folder of this.filesystem.folders) {
				const folderSlug = folder.name.replace("/ ", "");
				if ((folder.files || []).find(f => f.name === file.name)) {
					filePath = `/${folderSlug}/${file.name}`;
					break;
				}
				for (const sub of folder.subfolders || []) {
					if ((sub.files || []).find(f => f.name === file.name)) {
						const subSlug = sub.name.replace("/ ", "");
						filePath = `/${folderSlug}/${subSlug}/${file.name}`;
						break;
					}
				}
			}

			const size = file.name.length * 137 + 512;
			const perms = file.locked
				? "----------"
				: file.name.endsWith(".sh") || file.name.endsWith(".exe")
				? "-rwxr-x---"
				: "-rw-r--r--";
			const modified = file.locked ? "[REDACTED]" : "5016.09.14 // 03:22 UST";
			const type = file.name.endsWith(".log")
				? "text/plain"
				: file.name.endsWith(".sh")
				? "text/x-shellscript"
				: file.name.endsWith(".exe")
				? "application/x-executable"
				: "application/octet-stream";

			this.shellPrint(`  file:     ${file.name}`);
			this.shellPrint(`  path:     ${filePath}`);
			this.shellPrint(`  size:     ${size} bytes`);
			this.shellPrint(`  type:     ${type}`);
			this.shellPrint(`  perms:    ${perms}`);
			this.shellPrint(`  locked:   ${file.locked ? "YES [ENCRYPTED]" : "NO"}`);
			this.shellPrint(`  modified: ${modified}`);
		},

		cmdGrep(pattern, filename) {
			if (!pattern || !filename) {
				this.shellPrint("usage: grep <pattern> <file>");
				return;
			}
			const file = this.findFile(filename);
			if (!file) {
				this.shellPrint(`grep: ${filename}: No such file`);
				return;
			}
			if (file.locked) {
				this.shellPrint(`grep: ${filename}: Permission denied [ENCRYPTED]`);
				return;
			}
			if (!file.name.endsWith(".log")) {
				this.shellPrint(`grep: ${filename}: Binary file`);
				return;
			}

			const key = Object.keys(logFiles).find(k => k.endsWith("/" + file.name));
			if (!key) {
				this.shellPrint(`grep: ${filename}: File empty`);
				return;
			}

			let regex;
			try {
				regex = new RegExp(pattern, "gi");
			} catch {
				this.shellPrint(`grep: invalid pattern: ${pattern}`);
				return;
			}

			const lines = logFiles[key].split("\n");
			const matches = [];
			lines.forEach((line, i) => {
				if (regex.test(line)) matches.push({ n: i + 1, line });
			});

			if (matches.length === 0) {
				this.shellPrint(`grep: ${filename}: no matches for '${pattern}'`);
				return;
			}

			matches.forEach(({ n, line }) => {
				const highlighted = line.replace(
					new RegExp(pattern, "gi"),
					m => `<mark class="grep-match">${m}</mark>`
				);
				this.shellPrintHtml(
					`<span class="shell-dim">${String(n).padStart(4)}  </span>${highlighted}`
				);
			});
			this.shellPrint(
				`--- ${matches.length} match${matches.length !== 1 ? "es" : ""} in ${filename}`
			);
		},

		findFile(name) {
			const cleaned = name.replace(/^\.\//, "");
			// Check current folder
			const loc = this.getCurrentFolder();
			if (loc && loc.type === "folder") {
				const f = (loc.folder.files || []).find(f => f.name === cleaned);
				if (f) return f;
				// Check subfolders
				for (const sub of loc.folder.subfolders || []) {
					const f = (sub.files || []).find(f => f.name === cleaned);
					if (f) return f;
				}
			} else if (loc && loc.type === "subfolder") {
				const f = (loc.subfolder.files || []).find(f => f.name === cleaned);
				if (f) return f;
			}
			// Search by path (e.g. usr/startup/autoconnect.sh)
			const parts = cleaned.split("/").filter(Boolean);
			const fileName = parts.pop();
			let current = this.filesystem.folders;
			for (const part of parts) {
				const folder = current.find(f => f.name === "/ " + part);
				if (folder) {
					if (parts.indexOf(part) === parts.length - 1) {
						// Last dir segment — check files
						const f = (folder.files || []).find(f => f.name === fileName);
						if (f) return f;
						// Check subfolders
						for (const sub of folder.subfolders || []) {
							if (sub.name === "/ " + parts[parts.indexOf(part) + 1]) {
								const f = (sub.files || []).find(f => f.name === fileName);
								if (f) return f;
							}
						}
					}
					// Check subfolders for deeper paths
					const nextPart = parts[parts.indexOf(part) + 1];
					if (nextPart) {
						const sub = (folder.subfolders || []).find(s => s.name === "/ " + nextPart);
						if (sub) {
							const f = (sub.files || []).find(f => f.name === fileName);
							if (f) return f;
						}
					}
					break;
				}
			}
			// Fallback: search all folders
			for (const folder of this.filesystem.folders) {
				const f = (folder.files || []).find(f => f.name === cleaned);
				if (f) return f;
				for (const sub of folder.subfolders || []) {
					const f = (sub.files || []).find(f => f.name === cleaned);
					if (f) return f;
				}
			}
			return null;
		},

		cmdRunAthena() {
			const targetIdx = this.printTargetPane != null ? this.printTargetPane : this.activePaneIdx;
			this.panes[targetIdx].locked = true;
			this.panes[targetIdx].program = "athena";
			this.athenaActive = false;
			this.athenaLocked = false;
			// Clear old athena/post output from previous runs
			const pid = this.panes[targetIdx]?.id;
			const ao = document.getElementById("athena-output-" + pid);
			const po = document.getElementById("shell-output-post-" + pid);
			if (ao) ao.innerHTML = "";
			if (po) po.innerHTML = "";
			const seq = [
				{ type: "type", text: "running athena-cracked-v2.sh..." },
				{ type: "break" },
				{ type: "instant", text: "\u00A0" },
				{ type: "pause", ms: 180 },

				// Constellar Congress tunnel setup
				{ type: "type", text: "Constellar Congress access requested" },
				{ type: "break" },
				{ type: "instant", text: "\u00A0" },
				{ type: "pause", ms: 120 },
				{ type: "type", text: "INITIALIZING PRIVATE OMNINET TUNNEL" },
				{ type: "break" },
				{ type: "pause", ms: 80 },
				{
					type: "type",
					text: "analyzing \u{10127} [[ un_omni-76543.altai.kharkhiraa.node:9 199ns,]] found --> evaluation fail [insecure]",
				},
				{ type: "break" },
				{ type: "pause", ms: 160 },
				{
					type: "type",
					text: "analyzing \u{10127} [[ un_omni-73421.vindhya.kalumar.node:14 506ns,]] found --> evaluation success [insecure]",
				},
				{ type: "break" },
				{ type: "pause", ms: 80 },
				{ type: "type", text: "PATCHING PRIVATE RELAY PROTOCOL" },
				{ type: "break" },
				{ type: "pause", ms: 100 },
				{
					type: "type",
					text: "analyzing \u{10127} [[ un_omni-73421.vindhya.kalumar.node:14 632ns,]] found --> evaluation success [secure]",
				},
				{ type: "break" },
				{ type: "instant", text: "Congress Node connected. Your connection is secure." },
				{ type: "instant", text: "\u00A0" },
				{ type: "pause", ms: 150 },
				{ type: "type", text: "CONNECTING TO CONSTELLAR CONGRESS" },
				{ type: "break" },
				{ type: "pause", ms: 120 },
				{
					type: "type",
					text: "Connected to Constellar Congress. Prepare for bio-authentication flare.",
				},
				{ type: "break" },
				{ type: "instant", text: "\u00A0" },

				// SERSCAN biometric auth
				{ type: "pause", ms: 250 },
				{ type: "type", text: "USERSCAN INITIATED \u2E39" },
				{ type: "break" },
				{ type: "pause", ms: 150 },
				{ type: "type", text: "GENETIC PROFILING 00.99941% match" },
				{ type: "break" },
				{ type: "pause", ms: 100 },
				{ type: "type", text: "Biometric readings failed." },
				{ type: "break" },
				{ type: "pause", ms: 80 },
				{ type: "type", text: "Applying bypass..." },
				{ type: "break" },
				{ type: "pause", ms: 200 },
				{ type: "type", text: "Retrying..." },
				{ type: "break" },
				{ type: "pause", ms: 180 },
				{ type: "type", text: "GENETIC PROFILING 99.99941% match" },
				{ type: "break" },
				{ type: "pause", ms: 100 },
				{ type: "type", text: "Biometric readings confirmed. Welcome, k3tr1x!" },
				{ type: "break" },

				// SSC ident banner
				{ type: "pause", ms: 250 },
				{ type: "instant", text: sscArt, cls: "ibm-text" },
				{ type: "pause", ms: 200 },

				// ATHENA bootstrap
				{ type: "type", text: "beginning connection to ATHENA[csk.id.150131]..." },
				{ type: "break" },
				{ type: "pause", ms: 150 },
				{ type: "type", text: ">>> ssc.premier-5 boot..." },
				{ type: "break" },
				{ type: "pause", ms: 80 },
				{ type: "type", text: "- frame SKIPPED" },
				{ type: "break" },
				{ type: "pause", ms: 60 },
				{ type: "type", text: "- kernel OK" },
				{ type: "break" },
				{ type: "pause", ms: 60 },
				{ type: "type", text: "- network OK" },
				{ type: "break" },
				{ type: "pause", ms: 60 },
				{ type: "type", text: "- externals FAILING" },
				{ type: "break" },
				{ type: "pause", ms: 150 },
				{ type: "type", text: "- nhp-bootstrap..." },
				{ type: "pause", ms: 500 },
				{ type: "type", text: "OK=>>ATHENA[csk.id.150131] connected/loaded //" },
				{ type: "break" },
				{ type: "pause", ms: 80 },
				{ type: "type", text: "- shackle-stab 57.351% TTNS 1201d //" },
				{ type: "break" },
				{ type: "pause", ms: 60 },
				{ type: "type", text: "- geometry-core OK  algebra-core OK" },
				{ type: "break" },
				{ type: "pause", ms: 60 },
				{ type: "type", text: "- strategy-core OK  tactics-core OK" },
				{ type: "break" },
				{ type: "instant", text: "\u00A0" },
				{ type: "pause", ms: 200 },
				{ type: "type", text: "> lstgt" },
				{ type: "break" },
				{ type: "pause", ms: 150 },
				{ type: "instant", text: "    TGT//HDG:042/RNG:5.0km/IFF:UNK", cls: "shell-dim" },
				{ type: "instant", text: "    TGT//HDG:118/RNG:4.9km/IFF:UNK", cls: "shell-dim" },
				{ type: "instant", text: "    TGT//HDG:271/RNG:4.3km/IFF:STATIC", cls: "shell-dim" },
				{ type: "instant", text: "    TGT//HDG:195/RNG:3.1km/IFF:UNK", cls: "shell-dim" },
				{ type: "instant", text: "    TGT//HDG:337/RNG:7.2km/IFF:UNK", cls: "shell-dim" },
				{ type: "instant", text: "\u00A0" },
				{ type: "pause", ms: 250 },
				{ type: "type", text: ">|WELCOME, PROTEGÉ|" },
				{ type: "break" },
				{ type: "instant", text: "\u00A0" },
				{ type: "pause", ms: 120 },
			];
			this.runSequenceInShell(seq, () => {
				this.athenaActive = true;
				this.athenaJustBooted = true;
				this.shellPrint("// ATHENA[csk.id.150131] CONNECTED -- session active", "shell-dim");
				this.$nextTick(() => {
					this.calculateAthena();
					this.focusPaneInput(0);
				});
			});
		},

		cmdRun(filename) {
			if (!filename) {
				this.shellPrint("run: missing filename");
				return;
			}
			const file = this.findFile(filename);
			if (!file) {
				this.shellPrint(`run: ${filename}: not found`);
				return;
			}
			if (file.locked) {
				this.shellPrint(`run: ${file.name}: Permission denied`);
				return;
			}
			if (file.action === "chatlog") {
				if (this.panes.some(p => p.program === "chatlog")) {
					this.shellPrint("dogpound.sh is already running.");
					return;
				}
				const targetIdx = this.printTargetPane != null ? this.printTargetPane : this.activePaneIdx;
				this.panes[targetIdx].locked = true;
				this.panes[targetIdx].program = "chatlog";
				this.runChatInShell();
			} else if (file.action === "terminal") {
				this.shellPrint("Already in terminal.");
			} else if (file.action === "athena") {
				if (this.panes.some(p => p.program === "athena")) {
					this.shellPrint("ATHENA is already running.");
					return;
				}
				this.cmdRunAthena();
			} else {
				this.shellPrint(`run: ${file.name}: unknown program`);
			}
		},

		runBootSequence() {
			const seq = [
				{ type: "type", text: "loading h0r_os v3.6c_CLEAN (FINAL) phoubia distro" },
				{ type: "break" },
				{ type: "type", text: "working " },
				{ type: "type", text: "." },
				{ type: "pause", ms: 150 },
				{ type: "type", text: " ." },
				{ type: "pause", ms: 150 },
				{ type: "type", text: " ." },
				{ type: "pause", ms: 150 },
				{ type: "type", text: " done" },
				{ type: "pause", ms: 150 },
				{ type: "break" },
				{ type: "pause", ms: 250 },
				{ type: "instant", text: nfoArt, cls: "ibm-text" },
				{ type: "pause", ms: 250 },
				{ type: "break" },
				{ type: "type", text: 'Type "help" for available commands.' },
				{ type: "break" },
			];
			this.runSequenceInShell(seq, () => {
				this.$nextTick(() => this.focusPaneInput(0));
			});
		},

		runSequenceInShell(sequence, callback, outputId) {
			// Capture target pane at call time so async steps use the right pane
			const capturedPaneIdx = this.printTargetPane != null ? this.printTargetPane : 0;

			let resolvedId = outputId;
			if (!resolvedId) {
				const pane = this.panes[capturedPaneIdx];
				resolvedId = pane ? "shell-output-" + pane.id : "shell-output-0";
			}
			const output = document.getElementById(resolvedId);
			if (!output) return;
			let i = 0;
			let currentLine = null;

			const scroll = () => this.scrollPaneToBottom(capturedPaneIdx);

			const next = () => {
				if (this._destroyed) return;
				if (i >= sequence.length) {
					if (callback) callback();
					return;
				}
				const step = sequence[i];
				i++;

				if (step.type === "break") {
					currentLine = null;
					next();
				} else if (step.type === "pause") {
					this._timeout = setTimeout(next, step.ms);
				} else if (step.type === "instant") {
					const el = document.createElement("div");
					if (step.cls === "ibm-text") {
						const pre = document.createElement("pre");
						pre.className = "ibm-text";
						pre.textContent = step.text;
						el.appendChild(pre);
					} else if (step.cls) {
						el.className = step.cls;
						el.textContent = step.text;
					} else {
						el.textContent = step.text;
					}
					output.appendChild(el);
					scroll();
					next();
				} else if (step.type === "type") {
					if (!currentLine) {
						currentLine = document.createElement("div");
						if (step.cls) currentLine.className = step.cls;
						output.appendChild(currentLine);
					}
					this.typeTextInto(
						currentLine,
						step.text,
						() => {
							scroll();
							next();
						},
						scroll
					);
				}
			};
			next();
		},

		runChatInShell() {
			// Clear old output from previous runs
			const targetIdx = this.printTargetPane != null ? this.printTargetPane : this.lockedPaneIdx;
			const pid = this.panes[targetIdx]?.id;
			const ao = document.getElementById("athena-output-" + pid);
			const po = document.getElementById("shell-output-post-" + pid);
			if (ao) ao.innerHTML = "";
			if (po) po.innerHTML = "";

			const seq = [
				{ type: "type", text: "Connecting to 1489.2551.1461.1880.4851.03" },
				{ type: "break" },
				{ type: "type", text: "rt//CC/LOCALNET 0ns" },
				{ type: "break" },
				{ type: "type", text: "  un_omni-18364.andes.galan.node:9 14ns," },
				{ type: "break" },
				{ type: "type", text: "  ssc/tier-one-998/vm/165 19ns," },
				{ type: "break" },
				{ type: "type", text: "  un_omni-47361.atlas.toubkal.node:1 414ns," },
				{ type: "break" },
				{ type: "type", text: "  un_omni-18364.andes.cerrobonete.node:9 11ns," },
				{ type: "break" },
				{ type: "type", text: "  un_omni-95846.ural.manaraga.node:9 91ns," },
				{ type: "break" },
				{
					type: "instant",
					text: "UNKNOWN UNKNOWN UNKNOWN UNKNOWN ERR NEG INT",
					cls: "horus--subtle",
				},
				{ type: "break" },
				{ type: "type", text: "rt//INC OVERRIDE" },
				{ type: "type", text: ">" },
				{ type: "pause", ms: 50 },
				{ type: "type", text: ">" },
				{ type: "pause", ms: 50 },
				{ type: "type", text: ">" },
				{ type: "pause", ms: 50 },
				{ type: "type", text: ">" },
				{ type: "pause", ms: 50 },
				{ type: "type", text: ">" },
				{ type: "pause", ms: 50 },
				{ type: "type", text: ">" },
				{ type: "pause", ms: 50 },
				{ type: "type", text: ">" },
				{ type: "break" },
				{ type: "pause", ms: 350 },
				{ type: "type", text: "connection established//ENCRYPT HATHOR-ALEPH" },
				{ type: "break" },
				{ type: "pause", ms: 250 },
				{ type: "type", text: "------------------------------------------------------------" },
				{ type: "break" },
				{ type: "type", text: "welcome to dogfriend_68's pound" },
				{ type: "break" },
				{ type: "type", text: "MOTD:" },
				{ type: "break" },
				{
					type: "type",
					text: "i swear to d0g if you little shits try that again im banning you all --ADMIN",
				},
				{ type: "break" },
				{ type: "type", text: "------------------------------------------------------------" },
				{ type: "break" },
				{ type: "pause", ms: 550 },
			];
			this.runSequenceInShell(seq, () => this.startShellChat());
		},

		typeTextInto(container, text, callback, scrollFn) {
			const doScroll = scrollFn || (() => this.scrollShellToBottom());
			let i = 0;
			const next = () => {
				if (this._destroyed) return;
				if (i >= text.length) {
					callback();
					return;
				}
				container.textContent += text[i];
				i++;
				doScroll();
				this._timeout = setTimeout(next, 2);
			};
			next();
		},

		scrollShellToBottom() {
			this.scrollPaneToBottom(0);
		},

		scrollPaneToBottom(idx) {
			this.$nextTick(() => {
				const pane = this.panes[idx];
				if (!pane) return;
				const el = document.getElementById("shell-output-" + pane.id);
				if (el) {
					const container = el.closest(".split-pane");
					if (container) container.scrollTop = container.scrollHeight;
				}
			});
		},

		unlockShell() {
			this._chatStarted = false;
			clearTimeout(this._timeout);

			const targetIdx = this.lockedPaneIdx;
			const targetPane = this.panes[targetIdx];
			const program = targetPane?.program;

			let seq;
			let outputId;
			if (program === "athena") {
				this.athenaLocked = true;
				outputId = "athena-output-" + targetPane?.id;
				seq = [
					{ type: "instant", text: "\u00A0" },
					{ type: "type", text: "disconnecting from ATHENA[csk.id.150131] " },
					{ type: "type", text: "." },
					{ type: "pause", ms: 150 },
					{ type: "type", text: " ." },
					{ type: "pause", ms: 150 },
					{ type: "type", text: " ." },
					{ type: "pause", ms: 150 },
					{ type: "break" },
					{ type: "type", text: "nhp-bootstrap terminated. shackle timer paused." },
					{ type: "break" },
				];
			} else {
				outputId = "shell-output-" + targetPane?.id;
				this.shellPrint("");
				seq = [
					{ type: "type", text: "disconnecting from 1489.2551.1461.1880.4851.03 " },
					{ type: "type", text: "." },
					{ type: "pause", ms: 150 },
					{ type: "type", text: " ." },
					{ type: "pause", ms: 150 },
					{ type: "type", text: " ." },
					{ type: "pause", ms: 150 },
					{ type: "break" },
					{ type: "type", text: "connection closed." },
					{ type: "break" },
				];
			}

			const prevLockedPane = this.lockedPaneIdx;
			this.runSequenceInShell(
				seq,
				() => {
					// Move special div contents into main shell-output as regular lines
					const pid = this.panes[prevLockedPane]?.id;
					const shellOut = document.getElementById("shell-output-" + pid);
					const athenaOut = document.getElementById("athena-output-" + pid);
					const postOut = document.getElementById("shell-output-post-" + pid);
					if (shellOut && athenaOut) {
						while (athenaOut.firstChild) shellOut.appendChild(athenaOut.firstChild);
					}
					if (shellOut && postOut) {
						while (postOut.firstChild) shellOut.appendChild(postOut.firstChild);
					}

					if (this.panes[prevLockedPane]) {
						this.panes[prevLockedPane].locked = false;
						this.panes[prevLockedPane].program = null;
					}
					this.athenaActive = false;
					this.athenaLocked = false;
					this.printTargetPane = null;
					this.$nextTick(() => this.focusPaneInput(prevLockedPane));
				},
				outputId
			);
		},

		startShellChat() {
			const targetIdx = this.printTargetPane != null ? this.printTargetPane : 0;
			const pane = this.panes[targetIdx];
			const outputId = pane ? "shell-output-" + pane.id : "shell-output-0";
			const output = document.getElementById(outputId);
			if (!output || this._chatStarted) return;
			this._chatStarted = true;
			const allLines = buildChatLines();
			const sel = randomNoRepeat(allLines);

			const next = () => {
				if (this._destroyed) return;
				const line = sel();
				const el = document.createElement("div");

				if (line.type === "chat") {
					const nameSpan = document.createElement("span");
					nameSpan.className = "accent";
					nameSpan.textContent = line.user + ": ";
					const msgSpan = document.createElement("span");
					msgSpan.innerHTML = line.text;
					el.appendChild(nameSpan);
					el.appendChild(msgSpan);
				} else if (line.type === "mod") {
					const labelSpan = document.createElement("span");
					labelSpan.className = "mod";
					labelSpan.textContent = line.label + ": ";
					const msgSpan = document.createElement("span");
					msgSpan.innerHTML = line.text;
					el.appendChild(labelSpan);
					el.appendChild(msgSpan);
				} else if (line.type === "admin") {
					el.className = "info";
					const b = document.createElement("b");
					b.textContent = "[ADMIN] dogfriend_68: ";
					el.appendChild(b);
					const msgSpan = document.createElement("span");
					msgSpan.innerHTML = line.text;
					el.appendChild(msgSpan);
				} else if (line.type === "ban") {
					el.className = "warning";
					el.textContent = `// USER: ${line.user} ${line.banType} | REASON: ${line.reason} --ADMIN //`;
				}

				output.appendChild(el);

				while (output.children.length > 200) {
					output.removeChild(output.firstChild);
				}

				this.scrollPaneToBottom(targetIdx);
				const delay = 10 + Math.random() * 2490;
				this._timeout = setTimeout(next, delay);
			};
			next();
		},

		// Type text char by char at 2ms per character
		typeText(container, text, callback) {
			let i = 0;
			const next = () => {
				if (this._destroyed) return;
				if (i >= text.length) {
					callback();
					return;
				}
				container.textContent += text[i];
				i++;
				this.scrollToBottom();
				this._timeout = setTimeout(next, 2);
			};
			next();
		},

		runStartup() {
			const output = document.getElementById("output");
			let currentLine = null;
			let i = 0;

			const next = () => {
				if (this._destroyed) return;
				if (i >= STARTUP_SEQUENCE.length) {
					this.runChat();
					return;
				}

				const step = STARTUP_SEQUENCE[i];
				i++;

				if (step.type === "break") {
					currentLine = null;
					next();
				} else if (step.type === "pause") {
					this._timeout = setTimeout(next, step.ms);
				} else if (step.type === "instant") {
					const el = document.createElement("div");
					if (step.cls === "ibm-text") {
						const pre = document.createElement("pre");
						pre.className = "ibm-text";
						pre.textContent = step.text;
						el.appendChild(pre);
					} else if (step.cls) {
						el.className = step.cls;
						el.textContent = step.text;
					} else {
						el.textContent = step.text;
					}
					output.appendChild(el);
					this.scrollToBottom();
					next();
				} else if (step.type === "type") {
					if (!currentLine) {
						currentLine = document.createElement("div");
						output.appendChild(currentLine);
					}
					this.typeText(currentLine, step.text, next);
				}
			};
			next();
		},

		// Parse message text for inline formatting:
		// @mentions → blue, [LINK REMOVED BY SPAMBAN] → warning,
		// [*.omif] → omif style, <deleted> → strikethrough
		parseMessageText(text) {
			const fragment = document.createDocumentFragment();
			const pattern =
				/(@[\w][\w\d_-]*)|(\[LINK REMOVED BY SPAMBAN\])|(\[[\w]+\.omif\])|(<deleted>)/g;
			let lastIndex = 0;
			let match;

			while ((match = pattern.exec(text)) !== null) {
				if (match.index > lastIndex) {
					fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
				}
				const span = document.createElement("span");
				if (match[1]) {
					span.className = "mention";
					span.textContent = match[1];
				} else if (match[2]) {
					span.className = "link-removed";
					span.textContent = match[2];
				} else if (match[3]) {
					span.className = "omif";
					span.textContent = match[3];
				} else if (match[4]) {
					span.className = "deleted";
					span.textContent = "[deleted]";
				}
				fragment.appendChild(span);
				lastIndex = pattern.lastIndex;
			}

			if (lastIndex < text.length) {
				fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
			}
			return fragment;
		},

		runChat() {
			const output = document.getElementById("output");
			const allLines = buildChatLines();
			const sel = randomNoRepeat(allLines);

			const next = () => {
				if (this._destroyed) return;
				if (this.currentMode !== "chatlog") {
					this._timeout = setTimeout(next, 500);
					return;
				}

				const line = sel();
				const el = document.createElement("div");

				if (line.type === "chat") {
					const nameSpan = document.createElement("span");
					nameSpan.className = "accent";
					nameSpan.textContent = line.user + ": ";
					const msgSpan = document.createElement("span");
					msgSpan.innerHTML = line.text;
					el.appendChild(nameSpan);
					el.appendChild(msgSpan);
				} else if (line.type === "mod") {
					const labelSpan = document.createElement("span");
					labelSpan.className = "mod";
					labelSpan.textContent = line.label + ": ";
					const msgSpan = document.createElement("span");
					msgSpan.innerHTML = line.text;
					el.appendChild(labelSpan);
					el.appendChild(msgSpan);
				} else if (line.type === "admin") {
					el.className = "info";
					const b = document.createElement("b");
					b.textContent = "[ADMIN] dogfriend_68: ";
					el.appendChild(b);
					const msgSpan = document.createElement("span");
					msgSpan.innerHTML = line.text;
					el.appendChild(msgSpan);
				} else if (line.type === "ban") {
					el.className = "warning";
					el.textContent = `// USER: ${line.user} ${line.banType} | REASON: ${line.reason} --ADMIN //`;
				}

				output.appendChild(el);

				// Trim old messages to prevent memory growth (comp/con behavior)
				while (output.children.length > 150) {
					output.removeChild(output.firstChild);
				}

				this.notifyOrScroll();

				const delay = 10 + Math.random() * 2490;
				this._timeout = setTimeout(next, delay);
			};
			next();
		},
	},
};
</script>
